import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import Paginations from "components/common/Paginations";
import { NotificationStatusBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { showDeleteModal } from "store/slices/modalSlice";
import {
  getNotifications,
  resetFilters,
  setFilter,
  setIsScheduled,
} from "store/slices/notificationSlice";
import { NOTIFICATION_STATUS, NOTIFICATION_TYPE } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import { usePageParam } from "hooks/usePageParam";

const NotificationHistory = () => {
  const { t } = useTranslation();
  const { view, trash, edit, noPlans } = Icons || {};
  const dispatch = useDispatch();
  const [pageParam, setPageParam] = usePageParam();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("notifications");

  const {
    loading,
    isLoading,
    totalPages,
    currentPage,
    totalCount,
    notifications,
    filters: { perPage, pageNo, type, status, keyword, dates },
    isScheduled,
  } = useSelector((state) => state.Notification);

  useEffect(() => {
    canView && getAllData(pageParam);
  }, [canView, isScheduled]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    setPageParam(page || 1);

    const payload = {
      perPage,
      pageNo: page || pageNo,
      status: isReset ? "" : status,
      type: ["specific_customers", "all"],
      keyword: isResetKeyword ? "" : keyword || "",
      isScheduled: isScheduled,
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };

    dispatch(getNotifications(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: NOTIFICATION_STATUS.map((option) => ({
          ...option,
          name: t(option.name),
        })),
      },
    ],
    [t]
  );

  const columns = useMemo(
    () => [
      {
        header: t("Title"),
        key: "title",
        render: (value) => value?.en,
      },
      {
        header: t("Body"),
        key: "body",
        render: (value) => <p className="text-ellipsis-3">{value?.en}</p>,
      },
      {
        header: t("Audience"),
        key: "type",
        render: (value) =>
          t(
            NOTIFICATION_TYPE?.find((option) => option?.value === value)?.name
          ) || "-",
      },
      ...(isScheduled
        ? [
            {
              header: t("Scheduled Time"),
              key: "scheduledTime",
              render: (value) => (
                <div className="ltr_text">{formatUTCDate(value)}</div>
              ),
            },
          ]
        : []),

      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <NotificationStatusBadge status={value} t={t} />,
      },
    ],
    [t, isScheduled]
  );

  const renderActions = useCallback(
    (item) => (
      <>
        <Link to={`/notification/history/history-details?id=${item._id}`}>
          <img src={view} width={20} height={20} alt="view" />
        </Link>
        {isScheduled && canEdit && (
          <img
            src={edit}
            width={20}
            height={20}
            alt="edit"
            onClick={() =>
              navigate(`/notifications/edit-notification?id=${item._id}`)
            }
          />
        )}
        {isScheduled && canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt="delete"
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete Notification"),
                  message: t(
                    "Are you sure you want to delete this Notification?"
                  ),
                  itemId: item._id,
                  actionType: "NOTIFICATION",
                })
              );
            }}
          />
        )}
      </>
    ),
    [isScheduled]
  );

  if (!canView) return <NoPermission />;

  return (
    <div className="page-content">
      <PageHeader title={t("Notification History")}/>

      <div className="common_tabs my-4">
        <button
          type="button"
          className={`tabs_btn ${isScheduled ? "active" : ""}`}
          onClick={() => {
            dispatch(setIsScheduled());
          }}
        >
          {t("Scheduled")}
        </button>
        <button
          type="button"
          className={`tabs_btn ${!isScheduled ? "active" : ""}`}
          onClick={() => {
            dispatch(setIsScheduled());
          }}
        >
          {t("History")}
        </button>
      </div>

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Title")}
        filters={filtersList}
        selectedFilters={{ keyword, status }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
      />

      {loading && <TableShimmer rows={12} columns={8} />}

      {!loading && notifications?.length === 0 && (
        <NoDataFound title={t("No history found")} icon={noPlans} />
      )}
      {!loading && notifications?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={notifications}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default NotificationHistory;
